import React, { useMemo, useState } from 'react';
import {
 Plus,
 Edit,
 Trash2,
 AlertCircle,
 Loader2,
 ListChecks,
 RefreshCw,
} from 'lucide-react';
import { Button, CustomSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateMarketModal from '../../components/admin/CreateMarketModal';
import { useToastContext } from '../../contexts/ToastContext';
import {
 useAdminMarkets,
 useCreateMarket,
 useUpdateMarket,
 useDeleteMarket,
 useSetMarketStatus,
 useSyncMarketPacks,
} from '../../services/marketService';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const STATUS_FILTER_OPTIONS = [
 { value: 'all', label: 'All statuses' },
 { value: 'draft', label: 'Draft' },
 { value: 'active', label: 'Active' },
 { value: 'paused', label: 'Paused' },
];

const STATUS_OPTIONS = [
 { value: 'draft', label: 'Draft' },
 { value: 'active', label: 'Active' },
 { value: 'paused', label: 'Paused' },
];

const statusRadioActive = {
 draft: 'bg-gray-800 text-white border-gray-800',
 active: 'bg-emerald-600 text-white border-emerald-600',
 paused: 'bg-amber-600 text-white border-amber-600',
};

const statusRadioIdle =
 'bg-surface text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-surface-muted';

const MarketStatusRadios = ({ value, onChange, disabled = false }) => (
 <div
 role="radiogroup"
 aria-label="Market status"
 className="inline-flex items-center gap-1 p-0.5 rounded-full bg-gray-100/80 border border-border"
 onClick={(e) => e.stopPropagation()}
 >
 {STATUS_OPTIONS.map((opt) => {
 const selected = value === opt.value;
 return (
 <button
 key={opt.value}
 type="button"
 role="radio"
 aria-checked={selected}
 disabled={disabled}
 onClick={() => {
 if (!disabled && !selected) onChange(opt.value);
 }}
 className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed ${selected ? statusRadioActive[opt.value] : statusRadioIdle}`}
 >
 <span
 className={`h-2 w-2 rounded-full border shrink-0 ${selected ? 'bg-surface border-white/80 shadow-sm' : 'bg-transparent border-gray-400'}`}
 aria-hidden
 />
 {opt.label}
 </button>
 );
 })}
 </div>
);

const TAX_COLLECT_FILTER_OPTIONS = [
 { value: 'all', label: 'Tax: all' },
 { value: 'collecting', label: 'Collecting' },
 { value: 'not_collecting', label: 'Not collecting' },
];

const AdminMarkets = () => {
 const { success, error: showError } = useToastContext();
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [showEditModal, setShowEditModal] = useState(false);
 const [selected, setSelected] = useState(null);
 const [statusFilter, setStatusFilter] = useState('all');
 const [taxFilter, setTaxFilter] = useState('all');

 const { data, isLoading, error, refetch } = useAdminMarkets();
 const markets = data?.data || [];

 const filteredMarkets = useMemo(() => {
 return markets.filter((m) => {
 if (statusFilter !== 'all' && m.status !== statusFilter) return false;
 if (taxFilter === 'collecting' && !m.tax_collect_enabled) return false;
 if (taxFilter === 'not_collecting' && m.tax_collect_enabled) return false;
 return true;
 });
 }, [markets, statusFilter, taxFilter]);

 const createMutation = useCreateMarket({
 onSuccess: () => {
 success('Market created');
 setShowCreateModal(false);
 },
 onError: (err) => showError(err?.message || 'Failed to create market'),
 });

 const updateMutation = useUpdateMarket({
 onSuccess: () => {
 success('Market updated');
 setShowEditModal(false);
 setSelected(null);
 },
 onError: (err) => showError(err?.message || 'Failed to update market'),
 });

 const deleteMutation = useDeleteMarket({
 onSuccess: () => success('Market deleted'),
 onError: (err) => showError(err?.message || 'Failed to delete market'),
 });

 const statusMutation = useSetMarketStatus({
 onSuccess: () => success('Market status updated'),
 onError: (err) => showError(err?.message || 'Failed to update status'),
 });

 const syncPacksMutation = useSyncMarketPacks({
 onSuccess: (res) => {
 const d = res?.data || {};
 success(
 res?.message ||
 `Synced packs — created ${d.created ?? 0}, updated ${d.updated ?? 0}`
 );
 },
 onError: (err) => showError(err?.message || 'Failed to sync country packs'),
 });

 const packCodes = (data?.meta?.country_packs || []).map((p) => p.country_code).join(', ');

 const columns = [
 {
 key: 'display_name',
 label: 'Market',
 sortable: true,
 searchable: true,
 render: (row) => (
 <div className="min-w-0">
 <p className="text-sm font-semibold text-ink">{row.display_name}</p>
 <p className="text-xs text-ink-tertiary">
 {row.country?.name || row.country_code} · {row.country_code}
 </p>
 </div>
 ),
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 render: (row) => (
 <MarketStatusRadios
 value={row.status}
 disabled={statusMutation.isPending}
 onChange={(value) => statusMutation.mutate({ id: row.id, status: value })}
 />
 ),
 },
 {
 key: 'tax_module',
 label: 'Tax',
 render: (row) => (
 <div className="text-xs text-ink-secondary">
 <div className="font-medium">{row.tax_module || 'none'}</div>
 <div className="text-gray-500">{row.tax_engine_provider || 'none'}</div>
 {row.tax_collect_enabled ? (
 <div className="text-emerald-600 mt-0.5">
 Collecting{row.tax_rate_percent != null ? ` · ${row.tax_rate_percent}%` : ''}
 </div>
 ) : (
 <div className="text-gray-400 mt-0.5">Not collecting</div>
 )}
 </div>
 ),
 },
 {
 key: 'payment_rails',
 label: 'Payments',
 render: (row) => (
 <div className="text-xs text-ink-secondary">
 <div>{row.payment_rails}</div>
 <div className="text-gray-500">{row.default_display_currency}</div>
 </div>
 ),
 },
 {
 key: 'payout_hold_days',
 label: 'Hold',
 render: (row) => (
 <span className="text-sm tabular-nums text-ink">
 {row.payout_hold_days ?? 0}d
 </span>
 ),
 },
 {
 key: 'flags',
 label: 'Flags',
 render: (row) => (
 <div className="text-[11px] text-ink-tertiary max-w-[140px]">
 {(row.reporting_flags || []).join(', ') || '—'}
 </div>
 ),
 },
 ];

 const actions = [
 {
 label: 'Edit',
 icon: Edit,
 onClick: (row) => {
 setSelected(row);
 setShowEditModal(true);
 },
 title: 'Edit market',
 },
 {
 label: 'Delete',
 icon: Trash2,
 onClick: (row) => {
 if (window.confirm(`Delete market “${row.display_name}”?`)) {
 deleteMutation.mutate(row.id);
 }
 },
 title: 'Delete market',
 variant: 'danger',
 },
 ];

 if (error) {
 return (
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <AlertCircle className="mx-auto h-10 w-10 text-red-400 mb-3" />
 <p className="text-sm text-red-700 mb-4">{error.message || 'Failed to load markets'}</p>
 <Button className="rounded-md" onClick={() => refetch()}>Try again</Button>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Markets"
 subtitle="Enable countries where the marketplace can operate — tax packs, payment rails, and payout policy. Not hard-coded by IP."
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Button
 variant="secondary"
 className="rounded-md"
 disabled={syncPacksMutation.isPending}
 onClick={() => {
 if (
 window.confirm(
 'Sync registered country packs (CA, US, GH, GB, FR, NG, ZA)? Existing Active/Paused status is preserved.'
 )
 ) {
 syncPacksMutation.mutate({});
 }
 }}
 >
 {syncPacksMutation.isPending ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <RefreshCw className="w-4 h-4 mr-2" />
 )}
 Sync country packs
 </Button>
 <Button className="rounded-md" onClick={() => setShowCreateModal(true)}>
 <Plus className="w-4 h-4 mr-2" />
 Add market
 </Button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.markets?.title || 'Markets'}
 steps={ADMIN_PAGE_GUIDES.markets?.steps || [
 'Sync country packs to create draft markets (UK, France, Nigeria, South Africa, …).',
 'Keep status Draft until counsel/tax engine is ready, then set Active.',
 'Turn on tax collection only after the market’s tax engine is configured.',
 ]}
 icon={ListChecks}
 />

 <div className="rounded-lg border border-border/80 bg-surface-muted/60 px-4 py-3 text-sm text-ink/90">
 Registered packs{packCodes ? `: ${packCodes}` : ''} — sync adds missing drafts; geolocation never decides tax alone.
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl">
 <CustomSelect
 label="Status"
 value={statusFilter}
 onChange={setStatusFilter}
 options={STATUS_FILTER_OPTIONS}
 size="sm"
 />
 <CustomSelect
 label="Tax collection"
 value={taxFilter}
 onChange={setTaxFilter}
 options={TAX_COLLECT_FILTER_OPTIONS}
 size="sm"
 />
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-gray-500">Loading markets…</p>
 </div>
 ) : (
 <DataTable
 data={filteredMarkets}
 columns={columns}
 actions={actions}
 searchable
 sortable
 filterable={false}
 pageSize={20}
 showPagination
 searchPlaceholder="Search markets…"
 emptyMessage="No markets match these filters. Add Canada, USA, or Ghana to start."
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 {showCreateModal && (
 <CreateMarketModal
 isOpen={showCreateModal}
 onClose={() => setShowCreateModal(false)}
 onSave={(payload) => createMutation.mutate(payload)}
 isLoading={createMutation.isPending}
 />
 )}

 {showEditModal && selected && (
 <CreateMarketModal
 isOpen={showEditModal}
 isEdit
 market={selected}
 onClose={() => {
 setShowEditModal(false);
 setSelected(null);
 }}
 onSave={(payload) => updateMutation.mutate({ id: selected.id, data: payload })}
 isLoading={updateMutation.isPending}
 />
 )}
 </div>
 );
};

export default AdminMarkets;
